/* import __COLOCATED_TEMPLATE__ from './phone-dialler-contact-selectors.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type SearchableDocument from 'embercom/objects/inbox/searchable-document';
import type TeamSummary from 'embercom/objects/inbox/team-summary';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import { ChannelAvailabilityCode } from 'embercom/services/inbox2-teammate-activity-service';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import { isValidE164PhoneNumber } from 'embercom/utils/phone-number';
import type { PhoneReusableWorkflow } from 'embercom/objects/phone/reusable-workflow';
import type RouterService from '@ember/routing/router-service';
import type Session from 'embercom/services/session';
import safeWindowOpen from 'embercom/lib/safe-window-open';

type FavoriteChip =
  | {
      type: 'admin';
      id: number;
      name: string;
      admin: AdminSummary;
      onClick: () => void;
      onRemove: () => void;
    }
  | {
      type: 'team';
      id: number;
      name: string;
      team: TeamSummary;
      onClick: () => void;
      onRemove: () => void;
    }
  | { type: 'workflow'; id: number; name: string; onClick: () => void; onRemove: () => void }
  | {
      type: 'user';
      id: string;
      name: string;
      user: UserSummary;
      onClick: () => void;
      onRemove: () => void;
    };

const CONTACT_LIST_ORDER: ReadonlyArray<FavoriteChip['type']> = [
  'admin',
  'team',
  'user',
  'workflow',
];

const FAVORITE_TYPE_ORDER: Record<FavoriteChip['type'], number> = Object.fromEntries(
  CONTACT_LIST_ORDER.map((type, index) => [type, index]),
) as Record<FavoriteChip['type'], number>;

interface Signature {
  Element: HTMLElement;
  Args: {
    searchPlaceholder: string;
    showAdminsList: boolean;
    showTeamsList: boolean;
    showUsersList: boolean;
    showWorkflowsList?: boolean;
    workflowsList?: PhoneReusableWorkflow[];
    workflowsLoadFailed?: boolean;
    isActivelyCallingId?: string | number;
    isActivelyCalling: boolean;
    actionLabel: string;
    actionProcessingLabel: string;
    onAdminListActionClicked: (admin: AdminSummary) => void;
    onTeamListActionClicked: (team: TeamSummary) => void;
    onUserListActionClicked: (user: UserSummary) => void;
    onWorkflowListActionClicked: (workflow: PhoneReusableWorkflow) => void;
    showFavorites?: boolean;
    favoriteTransferTargets?: Array<{ type: string; id: string | number }>;
    onToggleFavorite?: (type: string, id: string | number) => void;
  };
}

export default class PhoneDiallerContactSelectors extends Component<Signature> {
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare intercomCallService: IntercomCallService;
  @service declare router: RouterService;
  @service declare session: Session;

  @tracked userSearchQuery = '';
  @tracked userInitialList: UserSummary[] = [];
  @tracked userSearchResults: UserSummary[] = [];
  @tracked isLoadingUserSearchResults = false;
  @tracked transferOptionsLoadFailed = false;
  @tracked userSearchLoadFailed = false;
  @tracked hasHydratedFavorites = false;

  get adminsForApp(): Array<AdminSummary> {
    let allAdmins = this.inbox2AssigneeSearch.searchableAdmins;
    let adminSummaryObjects = allAdmins.map(
      (adminSearchableDocument: SearchableDocument) => adminSearchableDocument.data as AdminSummary,
    );
    adminSummaryObjects = adminSummaryObjects.filter(
      (admin) =>
        !admin.isUnassignedAssignee &&
        admin.restrictedContexts?.length === 0 &&
        admin.adminStatus?.awayModeEnabled === false &&
        (admin.adminStatus?.channelAvailability === ChannelAvailabilityCode.Both ||
          admin.adminStatus?.channelAvailability === ChannelAvailabilityCode.Phone) &&
        admin.name.toLowerCase().includes(this.userSearchQuery.toLowerCase()),
    );
    return adminSummaryObjects;
  }

  get transferTeams(): Array<TeamSummary> {
    let teamSearchableDocuments = this.inbox2AssigneeSearch.searchableTeams;
    let teamSummaryObjects = teamSearchableDocuments.map(
      (teamSearchableDocument: SearchableDocument) => teamSearchableDocument.data as TeamSummary,
    );
    teamSummaryObjects = teamSummaryObjects.filter(
      (team) =>
        !team.isUnassignedAssignee &&
        team.name.toLowerCase().includes(this.userSearchQuery.toLowerCase()),
    );

    return teamSummaryObjects.sort((a, b) => a.name.localeCompare(b.name));
  }

  get transferWorkflows(): PhoneReusableWorkflow[] {
    if (!this.args.workflowsList) {
      return [];
    }

    return this.args.workflowsList.filter(
      (workflow) =>
        workflow.title?.toLowerCase().includes(this.userSearchQuery.toLowerCase()) ?? false,
    );
  }

  get filteredFavorites(): FavoriteChip[] {
    if (!this.args.showFavorites || !this.args.favoriteTransferTargets?.length) {
      return [];
    }
    let chips: FavoriteChip[] = [];
    for (let target of this.args.favoriteTransferTargets) {
      let chip = this.resolveFavoriteChip(target);
      if (chip) {
        chips.push(chip);
      }
    }
    return chips.sort((a, b) => FAVORITE_TYPE_ORDER[a.type] - FAVORITE_TYPE_ORDER[b.type]);
  }

  private resolveFavoriteChip(target: { type: string; id: string | number }): FavoriteChip | null {
    let onToggle = this.args.onToggleFavorite;
    switch (target.type) {
      case 'admin': {
        if (!this.args.showAdminsList) {
          return null;
        }
        let id = Number(target.id);
        let admin = this.adminsForApp.find((a) => Number(a.id) === id);
        if (!admin) {
          return null;
        }
        return {
          type: 'admin',
          id,
          name: admin.name,
          admin,
          onClick: () => this.args.onAdminListActionClicked(admin),
          onRemove: () => onToggle?.('admin', id),
        };
      }
      case 'team': {
        if (!this.args.showTeamsList) {
          return null;
        }
        let id = Number(target.id);
        let team = this.transferTeams.find((t) => Number(t.id) === id);
        if (!team) {
          return null;
        }
        return {
          type: 'team',
          id,
          name: team.name,
          team,
          onClick: () => this.args.onTeamListActionClicked(team),
          onRemove: () => onToggle?.('team', id),
        };
      }
      case 'workflow': {
        if (!this.args.showWorkflowsList) {
          return null;
        }
        let id = Number(target.id);
        let workflow = this.transferWorkflows.find((w) => Number(w.id) === id);
        if (!workflow) {
          return null;
        }
        return {
          type: 'workflow',
          id,
          name: workflow.title ?? '',
          onClick: () => this.args.onWorkflowListActionClicked(workflow),
          onRemove: () => onToggle?.('workflow', id),
        };
      }
      case 'user': {
        if (!this.args.showUsersList) {
          return null;
        }
        let id = String(target.id);
        let user = this.knownUsers.find((u) => String(u.id) === id);
        if (!user) {
          return null;
        }
        let query = this.userSearchQuery.toLowerCase();
        if (query && !user.displayAs.toLowerCase().includes(query)) {
          return null;
        }
        return {
          type: 'user',
          id,
          name: user.displayAs,
          user,
          onClick: () => this.args.onUserListActionClicked(user),
          onRemove: () => onToggle?.('user', id),
        };
      }
      default:
        return null;
    }
  }

  get knownUsers(): UserSummary[] {
    let seen = new Set<string>();
    let combined: UserSummary[] = [];
    for (let list of [this.userSearchResults, this.userInitialList]) {
      for (let user of list) {
        let id = String(user.id);
        if (!seen.has(id)) {
          seen.add(id);
          combined.push(user);
        }
      }
    }
    return combined;
  }

  get favoritesCount(): number {
    return this.visibleFavoriteTargetCount;
  }

  get visibleFavoriteTargetCount(): number {
    let targets = this.args.favoriteTransferTargets ?? [];
    return targets.filter((t) => this.isFavoriteTypeVisibleInMode(t.type)).length;
  }

  isFavoriteTypeVisibleInMode(type: string): boolean {
    if (type === 'admin') {
      return this.args.showAdminsList;
    }
    if (type === 'team') {
      return this.args.showTeamsList;
    }
    if (type === 'workflow') {
      return this.args.showWorkflowsList ?? false;
    }
    if (type === 'user') {
      return this.args.showUsersList;
    }
    return false;
  }

  get displayedFavorites(): FavoriteChip[] {
    return this.hasHydratedFavorites ? this.filteredFavorites : [];
  }

  get favoritePlaceholderSlots(): number[] {
    if (this.hasHydratedFavorites) {
      return [];
    }
    return Array.from({ length: this.visibleFavoriteTargetCount }, (_, i) => i);
  }

  get favoriteTargetIds(): string[] {
    let ids: string[] = [];
    for (let target of this.args.favoriteTransferTargets ?? []) {
      ids.push(`${target.type}-${target.id}`);
    }
    return ids;
  }

  get isRefreshingTransferOptions() {
    return taskFor(this.refreshTransferOptions).isRunning;
  }

  get isLoading() {
    if (this.isLoadingUserSearchResults) {
      return true;
    }

    if ((this.args.showTeamsList || this.args.showAdminsList) && this.isRefreshingTransferOptions) {
      return true;
    }

    return false;
  }

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    if (this.args.showTeamsList || this.args.showAdminsList) {
      taskFor(this.refreshTransferOptions).perform();
    }

    if (this.args.showUsersList) {
      taskFor(this.onQueryChanged).perform(this.userSearchQuery);
    }
  }

  @restartableTask *refreshTransferOptions(): TaskGenerator<void> {
    yield this.inbox2AssigneeSearch.loadAllAdminsAndTeams();
    this.transferOptionsLoadFailed = this.inbox2AssigneeSearch.loadFailed;
    this.markFavoritesHydratedIfReady();
  }

  markFavoritesHydratedIfReady() {
    if (this.hasHydratedFavorites) {
      return;
    }
    let needsAdminsTeams = this.args.showAdminsList || this.args.showTeamsList;
    let needsUsers = this.args.showUsersList;
    let adminsTeamsReady = !needsAdminsTeams || !this.isRefreshingTransferOptions;
    let usersReady = !needsUsers || this.userInitialList.length > 0;
    if (adminsTeamsReady && usersReady) {
      this.hasHydratedFavorites = true;
    }
  }

  @restartableTask *onQueryChanged(query: string): TaskGenerator<void> {
    this.userSearchQuery = query;
    this.userSearchResults = [];
    if (!query && this.userInitialList.length > 0) {
      this.userSearchResults = this.userInitialList;
      this.isLoadingUserSearchResults = false;
      return;
    }
    this.isLoadingUserSearchResults = true;
    this.userSearchLoadFailed = false;

    try {
      let users: UserSummary[] =
        yield this.inboxSearchSuggestionsService.getUserSuggestionsForDialler(
          this.userSearchQuery,
          10,
          'contains',
        );
      if (this.userInitialList.length === 0) {
        this.userInitialList = users;
      }
      this.userSearchResults = users;
      this.markFavoritesHydratedIfReady();

      if (users.length === 0) {
        this.intercomCallService.registerCallEvent({
          action: 'user_search',
          object: 'dialler_modal',
          place: 'user_search',
          context: 'no_results',
        });
      } else {
        this.intercomCallService.registerCallEvent({
          action: 'user_search',
          object: 'dialler_modal',
          place: 'user_search',
          context: 'results_available',
        });
      }
    } catch (e: unknown) {
      this.userSearchResults = [];
      this.userSearchLoadFailed = true;
    }
    this.isLoadingUserSearchResults = false;
  }

  checkPhoneNumberValid(userSummary: UserSummary) {
    return isValidE164PhoneNumber(userSummary.phone || '');
  }

  @action
  openWorkflowInNewTab(workflowId: number, event: MouseEvent) {
    event.stopPropagation();
    safeWindowOpen(
      this.router.urlFor(
        'apps.app.automation.workflows.edit',
        this.session.workspace.id,
        workflowId,
        {
          queryParams: {
            visual: 'enabled',
          },
        },
      ),
      '_blank',
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Calling::PhoneDiallerContactSelectors': typeof PhoneDiallerContactSelectors;
    'inbox2/calling/phone-dialler-contact-selectors': typeof PhoneDiallerContactSelectors;
  }
}
